import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import axios from "axios";
import CONSTANTS from "../utils/constants";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AdminAnalitica() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [totalSum, setTotalSum] = useState("0.00");
  const [orders, setOrders] = useState(0);
  const [visitors, setVisitors] = useState(0);

  // ✅ Her grafik için ayrı state
  const [priceMonth, setPriceMonth] = useState(new Date().toISOString().slice(0, 7));
  const [viewerMonth, setViewerMonth] = useState(new Date().toISOString().slice(0, 7));

  const priceChartRef = useRef(null);
  const priceChartInstance = useRef(null);
  const productChartRef = useRef(null);
  const productChartInstance = useRef(null);
  const categoryChartRef = useRef(null);
  const categoryChartInstance = useRef(null);
  const viewerChartRef = useRef(null);
  const viewerChartInstance = useRef(null);

  const getMonthName = (monthString) => {
    const [year, month] = monthString.split("-");
    const months = [
      t("jan"), t("feb"), t("mar"), t("apr"), t("may"), t("jun"),
      t("jul"), t("aug"), t("sep"), t("oct"), t("nov"), t("dec"),
    ];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  // ✅ Kart verilerini getir (priceMonth'a bağlı)
  const fetchCardData = async () => {
    try {
      const [sumRes, ordersRes, viewerRes] = await Promise.all([
        axios.get(`${CONSTANTS.baseRoute}/analitica/totalSum`, { params: { month: priceMonth } }),
        axios.get(`${CONSTANTS.baseRoute}/analitica/orders`, { params: { month: priceMonth } }),
        axios.get(`${CONSTANTS.baseRoute}/analitica/viewer`, { params: { month: priceMonth } }), // priceMonth kullanıyoruz
      ]);

      setTotalSum(sumRes.data.total_sum);
      setOrders(ordersRes.data.orders_count);
      setVisitors(viewerRes.data.viewer_count);
    } catch (error) {
      console.error("Kart verileri yüklenirken hata:", error);
    }
  };

  // ✅ Gelir Verilerini Getir
  const fetchPriceData = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/price`, {
        params: { month: priceMonth },
      });
      const data = response.data;
      if (!priceChartRef.current) return;

      const [year, monthNum] = priceMonth.split("-").map(Number);
      const daysInMonth = new Date(year, monthNum, 0).getDate();
      const actualData = Array(daysInMonth).fill(0);

      data.forEach((item) => {
        const day = new Date(item.date).getDate() - 1;
        if (day >= 0 && day < daysInMonth) actualData[day] = parseFloat(item.total_price) || 0;
      });

      const options = {
        chart: { type: "area", height: 400, zoom: { enabled: false } },
        series: [{ name: t("jemi_girdeji"), data: actualData }],
        xaxis: { categories: Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString()) },
        yaxis: {
          title: { text: t("aylyk_girdeji_m") },
          labels: { formatter: (val) => `${val.toFixed(0)} m` },
        },
        colors: ["#3B82F6"],
        stroke: { curve: "smooth", width: 3 },
        title: {
          text: `${t("gunluk_girdeji")} (${getMonthName(priceMonth)})`,
          style: { fontSize: "14px", color: "#3494E8" },
        },
      };

      if (priceChartInstance.current) priceChartInstance.current.destroy();
      priceChartInstance.current = new ApexCharts(priceChartRef.current, options);
      priceChartInstance.current.render();
    } catch (error) {
      console.error(error);
    }
  };

  // ✅ İzleyici Verilerini Getir
  const fetchViewerData = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/viewer-daily`, {
        params: { month: viewerMonth },
      });

      const data = response.data;
      if (!viewerChartRef.current) return;

      const [year, monthNum] = viewerMonth.split("-").map(Number);
      const daysInMonth = new Date(year, monthNum, 0).getDate();
      const actualData = Array(daysInMonth).fill(0);

      data.forEach((item) => {
        const day = new Date(item.date).getDate() - 1;
        if (day >= 0 && day < daysInMonth) actualData[day] = Number(item.count);
      });

      const options = {
        chart: { type: "area", height: 400, zoom: { enabled: false } },
        series: [{ name: t("ulanyjy"), data: actualData }],
        xaxis: { categories: Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString()) },
        yaxis: {
          title: { text: t("gunluk_ulanyjy") },
          labels: { formatter: (val) => Math.round(val) },
        },
        colors: ["#06B6D4"],
        stroke: { curve: "smooth", width: 3 },
        title: {
          text: `${t("gunluk_ulanyjy")} (${getMonthName(viewerMonth)})`,
          style: { fontSize: "14px", color: "#0891B2" },
        },
      };

      if (viewerChartInstance.current) viewerChartInstance.current.destroy();
      viewerChartInstance.current = new ApexCharts(viewerChartRef.current, options);
      viewerChartInstance.current.render();
    } catch (error) {
      console.error(error);
    }
  };

  // ✅ Ürün Grafiği (Aylık veri - priceMonth'a göre)
  const fetchProductData = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/product`, {
        params: { month: priceMonth } // Aylık filtreleme eklendi
      });
      
      if (productChartRef.current) {
        const counts = {};
        response.data.forEach(item => (counts[item.name_tm] = (counts[item.name_tm] || 0) + 1));
        const options = {
          chart: { type: "bar", height: 400 },
          series: [{ name: t("satylan_haryt"), data: Object.values(counts) }],
          xaxis: { categories: Object.keys(counts) },
          colors: ["#10B981"],
          title: { text: `${t("satylan_harytlar_su_ay")} (${getMonthName(priceMonth)})`, style: { color: "#059669" } },
        };
        if (productChartInstance.current) productChartInstance.current.destroy();
        productChartInstance.current = new ApexCharts(productChartRef.current, options);
        productChartInstance.current.render();
      }
    } catch (error) {
      console.error("Ürün grafiği hatası:", error);
    }
  };

  // ✅ Kategori Grafiği (Aylık veri - priceMonth'a göre)
  const fetchCategoryData = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/analitica/category`, {
        params: { month: priceMonth } // Aylık filtreleme eklendi
      });
      
      if (categoryChartRef.current) {
        const counts = {};
        response.data.forEach(item => (counts[item.category_name] = (counts[item.category_name] || 0) + 1));
        const options = {
          chart: { type: "bar", height: 400 },
          series: [{ name: t("satylan_kategoriya"), data: Object.values(counts) }],
          xaxis: { categories: Object.keys(counts) },
          colors: ["#F45142"],
          title: { text: `${t("en_kop_satylan_kategoriyalar")} (${getMonthName(priceMonth)})`, style: { color: "#F45142" } },
        };
        if (categoryChartInstance.current) categoryChartInstance.current.destroy();
        categoryChartInstance.current = new ApexCharts(categoryChartRef.current, options);
        categoryChartInstance.current.render();
      }
    } catch (error) {
      console.error("Kategori grafiği hatası:", error);
    }
  };

  // ✅ Tüm verileri güncelle (priceMonth değişince)
  useEffect(() => {
    fetchCardData();
    fetchPriceData();
    fetchProductData();
    fetchCategoryData();
  }, [priceMonth, i18n.language]);

  // ✅ Viewer grafiği (viewerMonth değişince)
  useEffect(() => {
    fetchViewerData();
  }, [viewerMonth, i18n.language]);

  // ✅ İlk yükleme
  useEffect(() => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    setPriceMonth(currentMonth);
    setViewerMonth(currentMonth);
  }, []);

  return (
    <div className="admin-main p-2 overflow-x-hidden">
      {/* Üst Kısım Navigasyon */}
      <div className="d-flex gap-2 align-items-center mb-2">
        <button className="btn admin-icon-btn" onClick={() => navigate(-1)}><i className="bi bi-chevron-left"></i></button>
        <button className="btn admin-icon-btn" onClick={() => navigate(+1)}><i className="bi bi-chevron-right"></i></button>
        <h5 className=" mb-0 fw-bold">{t("analitika")}</h5>
      </div>

      {/* Kartlar - priceMonth'a bağlı */}
      <div className="card border bg-light p-2 mb-3">
        <h5 className="mb-4 fw-bold">{t("sul_ay")} - {getMonthName(priceMonth)}</h5>
        <div className="row row-cols-1 row-cols-md-3 g-3 align-items-end">
          <div className="col">
            <div className="border shadow-sm rounded-2 p-3 bg-white">
              <h6 className="text-muted mb-0">{t("girdeji")}</h6>
              <h3 className="fw-bold mt-2 mb-0">{totalSum} M</h3>
            </div>
          </div>
          <div className="col">
            <div className="border shadow-sm rounded-2 p-3 bg-white">
              <h6 className="text-muted mb-0">{t("sargyt")}</h6>
              <h3 className="fw-bold mt-2 mb-0">{orders}</h3>
            </div>
          </div>
          <div className="col">
            <div className="border shadow-sm rounded-2 p-3 bg-white">
              <h6 className="text-muted mb-0">{t("ulanyjy")}</h6>
              <h3 className="fw-bold mt-2 mb-0">{visitors}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="border shadow-sm rounded-2 p-3 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 fw-bold">{t("gunluk_girdeji")}</h6>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 small text-muted">{t("ay_saylan")}:</label>
            <input
              type="month"
              className="form-control form-control-sm"
              value={priceMonth}
              onChange={(e) => setPriceMonth(e.target.value)}
              style={{ width: "150px" }}
            />
          </div>
        </div>
        <div ref={priceChartRef} style={{ minHeight: "400px" }}></div>
      </div>

      <div className="border shadow-sm rounded-2 p-3 mb-4">
        <div ref={productChartRef} style={{ minHeight: "400px" }}></div>
      </div>

      <div className="border shadow-sm rounded-2 p-3 mb-4">
        <div ref={categoryChartRef} style={{ minHeight: "400px" }}></div>
      </div>

      <div className="border shadow-sm rounded-2 p-3 mb-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 fw-bold">{t("gunluk_ulanyjy")}</h6>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0 small text-muted">{t("ay_saylan")}:</label>
            <input
              type="month"
              className="form-control form-control-sm"
              value={viewerMonth}
              onChange={(e) => setViewerMonth(e.target.value)}
              style={{ width: "150px" }}
            />
          </div>
        </div>
        <div ref={viewerChartRef} style={{ minHeight: "400px" }}></div>
      </div>
    </div>
  );
}